import React, {
	type ComponentPropsWithoutRef,
	type ElementType,
	Component,
	type ComponentType,
} from 'react';
import { styled } from '@compiled/react';
import type { PreloadedQuery } from 'react-relay';
import { type CreateUIAnalyticsEvent, withAnalyticsEvents } from '@atlaskit/analytics-next';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { boardMenuItems } from '@atlassian/jira-automation-menu/src/common/constants.tsx';
import type AutomationMenuDI from '@atlassian/jira-automation-menu/src/ui/main.tsx';
import type BreadcrumbRendererDI from '@atlassian/jira-breadcrumbs/src/breadcrumb-renderer/index.tsx';
import PageHeader from '@atlassian/jira-common-components-page-header/src/index.tsx';
import type { ProjectType } from '@atlassian/jira-common-constants/src/index.tsx';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales.tsx';
import { BOARDS_ITEM_TYPE } from '@atlassian/jira-favourite-change-provider/src/model/constants.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import ProjectCreateImportProgress from '@atlassian/jira-project-create-import-progress/src/index.tsx';
import type { viewBoardCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBoardCustomFiltersQuery.graphql';
import { toProjectKey, toProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { IssueId } from '@atlassian/jira-software-board-common/src/index.tsx';
import { BoardConnectToolbar } from '@atlassian/jira-software-connect-toolbar/src/ui/board-connect-toolbar/index.tsx';
import { StandupControlButtons } from '@atlassian/jira-standups/src/ui/control-buttons/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import type { boardFilterRefinementQuery } from '@atlassian/jira-relay/src/__generated__/boardFilterRefinementQuery.graphql.ts';
import { getIsSoftwareThemingM2Enabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import type { IssueKey } from '../../model/issue/issue-types.tsx';
import type {
	RapidViewId,
	ProjectId,
	ProjectKey,
	ProjectName,
} from '../../model/software/software-types.tsx';
import type CompleteSprintButtonDI from './complete-sprint-button/index.tsx';
import type ControlsBarDI from './controls-bar/index.tsx';
import type BoardOperationsDI from './extension/index.tsx';
import type FavouriteButtonDI from './favourite-button/index.tsx';
import FeedbackButton from './feedback-collector-button/index.tsx';
import { FullscreenButton } from './fullscreen-button/index.tsx';
import type MenuDI from './menu/index.tsx';
import type ReleaseVersionDI from './release-version/index.tsx';
import { Separator } from './separator/index.tsx';
import { JswShareButton } from './share-button/index.tsx';
import { SprintDetailsButton } from './sprint-controls/sprint-details/index.tsx';
import { SprintGoal } from './sprint-goal/index.tsx';
import type SprintRemainingDaysDI from './sprint-remaining-days/index.tsx';
import type TitleDI from './title/index.tsx';
import { shouldDisableStandup } from './utils.tsx';

type DIType<CT extends ElementType> = ComponentType<ComponentPropsWithoutRef<CT>>;

export type StateProps = {
	isCompleteSprintButtonVisible: boolean;
	hasManageSprintPermission: boolean;
	isMenuVisible: boolean;
	/**
	 * True if:
	 * - the "Hide menus" CMP display option is flipped to true; or
	 * - for Increment Planning board from Advanced Roadmaps
	 */
	isHeaderCompactMode: boolean;
	isCMPBoard: boolean;
	isJSWBoard: boolean;
	isIncrementPlanningBoard: boolean;
	isSprintRemainingDaysVisible: boolean;
	isSprintsEnabled: boolean;
	shouldShowStandupsFeature: boolean;
	hasActiveSprint: boolean;
	canManageAutomations: boolean;
	locale: Locale;
	baseUrl: string;
	rapidViewId: RapidViewId;
	Title: DIType<typeof TitleDI>;
	sprintGoal: string | null;
	ControlsBar: DIType<typeof ControlsBarDI>;
	Menu: DIType<typeof MenuDI>;
	ReleaseVersion: DIType<typeof ReleaseVersionDI>;
	CompleteSprintButton: DIType<typeof CompleteSprintButtonDI>;
	FavoriteButton: DIType<typeof FavouriteButtonDI>;
	SprintRemainingDays: DIType<typeof SprintRemainingDaysDI>;
	BoardOperations: DIType<typeof BoardOperationsDI>;
	BreadcrumbRenderer: DIType<typeof BreadcrumbRendererDI>;
	AutomationMenu: DIType<typeof AutomationMenuDI>;
	projectKey: ProjectKey;
	projectId: ProjectId;
	projectType: ProjectType;
	projectName: ProjectName;
	selectedIssueKeys: IssueKey[];
	selectedIssueIds?: IssueId[];
	isReleaseVersionVisible: boolean;
	boardName: string;
	shouldRenderAutomationButton: boolean;
	shouldRenderFavouriteButton: boolean;
	shouldRenderReleaseButton: boolean;
	shouldRenderFeedbackCollectorButton: boolean;
	shouldRenderFullscreenButton: boolean;
	shouldRenderViewSettingsCardDetailsOption: boolean;
	boardType: string;
	/** This can be removed when removing Capability.IS_TAB_NAVIGATION */
	isTabNavigation: boolean;
};

export type OwnProps = {
	customFiltersQueryRef: PreloadedQuery<viewBoardCustomFiltersQuery> | null | undefined;
	boardFilterRefinementQueryRef?: PreloadedQuery<boardFilterRefinementQuery> | null;
	isEmbedView: boolean;
	isActiveStandup: boolean;
	setIsActiveStandup: React.Dispatch<React.SetStateAction<boolean>>;
};

export type Props = StateProps & OwnProps;

// eslint-disable-next-line jira/react/no-class-components
export class Header extends Component<
	Props & { createAnalyticsEvent: CreateUIAnalyticsEvent; isThemed?: boolean }
> {
	static defaultProps = {
		sprintGoal: null,
		selectedIssueKeys: [],
		isThemed: false,
	};

	renderSprintGoalNavV3() {
		return this.props.sprintGoal && <SprintGoal goal={this.props.sprintGoal} />;
	}

	renderBaseActionsNavV3() {
		const {
			baseUrl,
			isCMPBoard,
			hasActiveSprint,
			isSprintsEnabled,
			isIncrementPlanningBoard,
			ReleaseVersion,
			isCompleteSprintButtonVisible,
			hasManageSprintPermission,
			CompleteSprintButton,
			FavoriteButton,
			isSprintRemainingDaysVisible,
			SprintRemainingDays,
			BoardOperations,
			AutomationMenu,
			rapidViewId,
			canManageAutomations,
			projectKey,
			projectId,
			projectType,
			selectedIssueKeys,
			selectedIssueIds,
			isReleaseVersionVisible,
			isHeaderCompactMode,
			isEmbedView,
			boardName,
			shouldRenderAutomationButton,
			shouldRenderFavouriteButton,
			shouldRenderReleaseButton,
			shouldRenderFeedbackCollectorButton,
			shouldRenderFullscreenButton,
			boardType,
			createAnalyticsEvent,
			isJSWBoard,
			isActiveStandup,
			setIsActiveStandup,
			shouldShowStandupsFeature,
		} = this.props;

		const automationMenu = shouldRenderAutomationButton && !isActiveStandup && (
			<AutomationMenu
				items={boardMenuItems}
				projectType={projectType}
				projectId={toProjectId(`${projectId}`)}
				projectKey={toProjectKey(projectKey)} // software type to shared type
				isSimplified // Ignored in project aware BoardMenu
				isUIF={isCMPBoard} // Can be set to true always eventually
				canManageAutomations={canManageAutomations}
				selectedIssueKeys={selectedIssueKeys}
				// Cast issueId as number[] for now, until we need AutomationMenu for ARJ Increment planning board
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				selectedIssueIds={selectedIssueIds as number[]}
				key="automationButton"
			/>
		);
		const favButton = shouldRenderFavouriteButton && !isActiveStandup && (
			<FavoriteButton
				key="favouriteButton"
				baseUrl={baseUrl}
				itemId={rapidViewId}
				itemType={BOARDS_ITEM_TYPE}
				favoriteItemName={boardName}
				tooltipPosition="top"
			/>
		);

		const fullscreenButton =
			shouldRenderFullscreenButton && !isEmbedView ? (
				<FullscreenButton
					boardId={Number(rapidViewId)}
					shouldRenderContent={!isActiveStandup}
					key="fullscreenButton"
				/>
			) : null;

		const showShareButton = !isIncrementPlanningBoard;

		const shareContentType = `${boardType}_board`;

		const onShareButtonClick = () => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			});
			fireUIAnalytics(analyticsEvent, 'shareContentType', {
				contentType: shareContentType,
			});
		};

		const shareButton = showShareButton && !isActiveStandup && (
			<JswShareButton
				poSpotlightShareButtonId="po-spotlight-share-button"
				projectId={toProjectId(`${projectId}`)}
				key="shareButton"
				shareContentType={shareContentType}
				onTriggerButtonClick={onShareButtonClick}
			/>
		);

		const sprintRemainingDays = !isHeaderCompactMode && isSprintRemainingDaysVisible && (
			<SprintRemainingDays key="SprintRemainingButton" />
		);

		const isStandupDisabled = shouldDisableStandup({
			boardType,
			isSprintsEnabled,
			hasActiveSprint,
		});

		const standupControlButton = !isIncrementPlanningBoard &&
			isJSWBoard &&
			shouldShowStandupsFeature &&
			expVal('jira-in-product-standups', 'is_enabled', false) && (
				<StandupControlButtons
					{...(fg('concurrent-rendering-fix-unique-list-key')
						? { key: 'StandupControlButton' }
						: {})}
					isStandupDisabled={isStandupDisabled}
					isActiveStandup={isActiveStandup}
					setIsActiveStandup={setIsActiveStandup}
					boardId={Number(rapidViewId)}
				/>
			);

		return (
			<ActionsWrapper>
				{shouldRenderFeedbackCollectorButton && <FeedbackButton />}
				{sprintRemainingDays}
				{isSprintRemainingDaysVisible && <Separator key="sprint-info-separator" />}
				{isVisualRefreshEnabled() ? (
					<Inline space="space.100" alignInline="center">
						{[automationMenu, favButton, shareButton, fullscreenButton, standupControlButton]}
					</Inline>
				) : (
					<IconButtonWrapper>
						{[automationMenu, favButton, shareButton, fullscreenButton, standupControlButton]}
					</IconButtonWrapper>
				)}
				{!isEmbedView && !isHeaderCompactMode && isCompleteSprintButtonVisible && (
					<CompleteSprintButton
						{...(isVisualRefreshEnabled() ? { overrideAppearance: 'primary' } : {})}
						isDisabled={!hasManageSprintPermission}
					/>
				)}
				{!isHeaderCompactMode && <BoardOperations />}
				{shouldRenderReleaseButton && !isHeaderCompactMode && isReleaseVersionVisible && (
					<ReleaseVersion rapidViewId={rapidViewId} />
				)}
				{!isHeaderCompactMode && isCMPBoard && (
					<BoardConnectToolbar boardId={rapidViewId} projectKey={projectKey} mode="work" />
				)}
			</ActionsWrapper>
		);
	}

	renderBaseActionsNavV4() {
		const {
			CompleteSprintButton,
			hasActiveSprint,
			isCompleteSprintButtonVisible,
			isEmbedView,
			isHeaderCompactMode,
			shouldRenderFeedbackCollectorButton,
			hasManageSprintPermission,
		} = this.props;

		const actions: Array<React.ReactNode> = [];

		if (isVisualRefreshEnabled()) {
			const shouldRenderCompleteSprintButton =
				!isEmbedView && !isHeaderCompactMode && isCompleteSprintButtonVisible;
			if (shouldRenderCompleteSprintButton) {
				actions.push(
					<CompleteSprintButton
						key="completeSprintButton"
						overrideAppearance="primary"
						isDisabled={!hasManageSprintPermission}
					/>,
				);
			}

			const shouldRenderSprintControls = hasActiveSprint; // note: this also checks if sprints are enabled
			if (shouldRenderSprintControls) {
				actions.push(<SprintDetailsButton key="sprintPopup" />);
			}

			if (shouldRenderFeedbackCollectorButton) {
				actions.push(<FeedbackButton key="feedbackButton" />);
			}
		} else {
			if (shouldRenderFeedbackCollectorButton) {
				actions.push(<FeedbackButton key="feedbackButton" />);
			}

			const shouldRenderSprintControls = hasActiveSprint; // note: this also checks if sprints are enabled
			if (shouldRenderSprintControls) {
				actions.push(<SprintDetailsButton key="sprintPopup" />);
			}

			const shouldRenderCompleteSprintButton =
				!isEmbedView && !isHeaderCompactMode && isCompleteSprintButtonVisible;
			if (shouldRenderCompleteSprintButton) {
				actions.push(
					<CompleteSprintButton
						key="completeSprintButton"
						isDisabled={!hasManageSprintPermission}
					/>,
				);
			}
		}

		if (actions.length === 0) {
			return null;
		}

		return <ActionsWrapper>{actions}</ActionsWrapper>;
	}

	renderMenu() {
		const {
			isEmbedView,
			isMenuVisible,
			Menu,
			boardType,
			isActiveStandup,
			hasActiveSprint,
			isSprintsEnabled,
			rapidViewId,
			setIsActiveStandup,
		} = this.props;
		if (!isMenuVisible || isEmbedView) {
			return null;
		}

		return (
			<Menu
				rapidViewId={Number(rapidViewId)}
				boardType={boardType}
				isActiveStandup={isActiveStandup}
				isSprintsEnabled={isSprintsEnabled}
				hasActiveSprint={hasActiveSprint}
				setIsActiveStandup={setIsActiveStandup}
			/>
		);
	}

	renderBaseActionsAndMenu() {
		return (
			<ActionsWrapper>
				{this.renderBaseActionsNavV3()}
				{this.renderMenu()}
			</ActionsWrapper>
		);
	}

	render() {
		const {
			ControlsBar,
			Title,
			BreadcrumbRenderer,
			isSprintsEnabled,
			hasActiveSprint,
			customFiltersQueryRef,
			boardFilterRefinementQueryRef,
			rapidViewId,
			isHeaderCompactMode,
			isIncrementPlanningBoard,
			shouldRenderViewSettingsCardDetailsOption,
			projectKey,
			projectName,
			isTabNavigation,
			isActiveStandup,
			setIsActiveStandup,
			boardType,
			isThemed,
		} = this.props;

		/**
		 * In Advanced Roadmaps, we're embedding the JSW board into an "Increment" page
		 * which already has a <PageHeader /> with its own breadcrumbs and call-to-action buttons
		 * therefore all we need is the <ControlsBar />, nothing else.
		 */
		if (isIncrementPlanningBoard) {
			return (
				<UFOSegment name="software-board-header">
					<IncrementPlanningHeaderWrapper>
						<ControlsBar
							customFiltersQueryRef={null}
							hasActiveSprint={false}
							isSprintsEnabled={false}
							leftHandSideActions={
								isTabNavigation && fg('remove_plans_fullscreen_button_in_tabs_nav')
									? null
									: this.renderBaseActionsNavV3()
							}
							rapidViewId={Number(rapidViewId)}
							showCardDetails={shouldRenderViewSettingsCardDetailsOption}
							isActiveStandup={isActiveStandup}
							setIsActiveStandup={setIsActiveStandup}
							boardType={boardType}
						>
							{this.renderMenu()}
						</ControlsBar>
					</IncrementPlanningHeaderWrapper>
				</UFOSegment>
			);
		}

		const breadcrumbs = (
			<BreadcrumbContainer data-fullscreen-id="fullscreen-board-breadcrumbs">
				<BreadcrumbRenderer />
				{projectKey && (
					<ProjectCreateImportProgress projectKey={projectKey} projectName={projectName} />
				)}
			</BreadcrumbContainer>
		);

		return (
			<UFOSegment name="software-board-header">
				{isTabNavigation ? (
					/* FY25 Nav v4 aka. Tab Nav caused the "reshuffle" of the header:
					 * - automationMenu, shareButton, fullscreenButton => now above the tabs
					 * - sprint details & controls => moved to a flyout under a new button
					 * - extensions / connect apps => moved into menu
					 * ALSO we remove the "isHeaderCompactMode" logic = moved outside of this func
					 */
					<HeaderWrapperNavV4 isThemed={isThemed}>
						<ControlsBar
							isSprintsEnabled={isSprintsEnabled}
							hasActiveSprint={hasActiveSprint}
							customFiltersQueryRef={customFiltersQueryRef}
							boardFilterRefinementQueryRef={boardFilterRefinementQueryRef}
							// @ts-expect-error TS2322 - RapidViewId is string but controls expect number
							rapidViewId={rapidViewId}
							leftHandSideActions={isVisualRefreshEnabled() ? this.renderBaseActionsNavV4() : null}
							showCardDetails={shouldRenderViewSettingsCardDetailsOption}
							isActiveStandup={isActiveStandup}
							setIsActiveStandup={setIsActiveStandup}
							boardType={boardType}
						>
							{!isVisualRefreshEnabled() && this.renderBaseActionsNavV4()}

							{this.renderMenu()}
						</ControlsBar>
					</HeaderWrapperNavV4>
				) : (
					<HeaderWrapperNavV3>
						<PageHeader
							truncateTitle
							disableTitleStyles
							bottomBar={
								<ControlsBar
									isSprintsEnabled={isSprintsEnabled}
									hasActiveSprint={hasActiveSprint}
									customFiltersQueryRef={customFiltersQueryRef}
									boardFilterRefinementQueryRef={boardFilterRefinementQueryRef}
									// @ts-expect-error TS2322 - RapidViewId is string but controls expect number
									rapidViewId={rapidViewId}
									leftHandSideActions={isHeaderCompactMode && this.renderBaseActionsNavV3()}
									showCardDetails={shouldRenderViewSettingsCardDetailsOption}
									isActiveStandup={isActiveStandup}
									setIsActiveStandup={setIsActiveStandup}
									boardType={boardType}
								>
									{isHeaderCompactMode && this.renderMenu()}
								</ControlsBar>
							}
							actions={!isHeaderCompactMode && this.renderBaseActionsAndMenu()}
							breadcrumbs={breadcrumbs}
						>
							{!isHeaderCompactMode && (
								<>
									<Title />
									{this.renderSprintGoalNavV3()}
								</>
							)}
						</PageHeader>
					</HeaderWrapperNavV3>
				)}
			</UFOSegment>
		);
	}
}

const HeaderWrapper = (props: Props & { createAnalyticsEvent: CreateUIAnalyticsEvent }) => {
	const { hasSoftwareTheming } = getIsSoftwareThemingM2Enabled('blurry-background')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useSoftwareProjectTheming()
		: { hasSoftwareTheming: false };

	return <Header {...props} isThemed={hasSoftwareTheming} />;
};

export default componentWithCondition(
	() => getIsSoftwareThemingM2Enabled('blurry-background'),
	withAnalyticsEvents()(HeaderWrapper),
	withAnalyticsEvents()(Header),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BreadcrumbContainer = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&.fullscreen': {
		display: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapperNavV3Old = styled.div({
	paddingTop: 0,
	paddingRight: token('space.500'),
	paddingBottom: 0,
	paddingLeft: token('space.500'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapperNavV3New = styled.div({
	paddingTop: 0,
	paddingRight: token('space.300'),
	paddingBottom: 0,
	paddingLeft: token('space.300'),
});

const HeaderWrapperNavV3 = componentWithCondition(
	isVisualRefreshEnabled,
	HeaderWrapperNavV3New,
	HeaderWrapperNavV3Old,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapperNavV4Old = styled.div({
	paddingTop: token('space.300'),
	paddingRight: token('space.500'),
	paddingBottom: 0,
	paddingLeft: token('space.500'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapperNavV4New = styled.div<{
	isThemed?: boolean;
}>(
	{
		paddingTop: token('space.250'),
		paddingRight: token('space.300'),
		paddingBottom: 0,
		paddingLeft: token('space.300'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isThemed = false }) =>
		isThemed && {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			background: Tokens.ELEVATION_SURFACE_CONTRAST,
			marginBottom: token('space.200'),
			position: 'relative',
			'&:before': {
				content: '""',
				position: 'absolute',
				height: '100%',
				width: '100%',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				backdropFilter: Tokens.BACKDROP_FILTER,
				top: 0,
				left: 0,
			},
		},
);

const HeaderWrapperNavV4 = componentWithCondition(
	isVisualRefreshEnabled,
	HeaderWrapperNavV4New,
	HeaderWrapperNavV4Old,
);
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IncrementPlanningHeaderWrapper = styled.div({
	paddingTop: 0,
	paddingRight: token('space.300'),
	paddingBottom: 0,
	paddingLeft: token('space.500'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconButtonWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});
